import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { plainToClass } from 'class-transformer';

class Response {
  data: any;
  status!: number;
  code!: number;
  message: string[] = [];
}

class Param {
  service: string = '';
  method: string = 'get';
  params: any = {};
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  private built(service: string): string {
    return environment.endpoint + service;
  }

  private parse(data: any, type: any): Response {
    let response = new Response();

    response.status = data.status;
    response.message = data.message;
    response.data = plainToClass(type, data.data);
    return response;
  }

  callReq(
    param: Param,
    successCallback: (response: any) => void,
    errorCallback: (error: any) => void
  ): void {
    param.method = param.method.toLowerCase();

    this.call(param.service, param.method, param.params).subscribe({
      next: (response: any) => {
        if (response.status == 200) {
          successCallback(response);
        } else {
          errorCallback(response.message);
        }
      },
      error: (error: any) => {
        errorCallback(error.message);
      },
    });
  }

  get(service: string, params: any = {}): Observable<any> {
    return this.http.get<Response>(this.built(service), params).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  // ! api callback method
  execute(
    service: string,
    success: (response: any) => void,
    failed: (error: any) => void,
    method: string = 'get',
    params: any = {}
  ): void {
    this.call(service, method, params).subscribe(
      (response) => {
        if (response.status === 200) {
          success(response);
        } else {
          failed(response.message);
        }
      },
      (error) => {
        failed(error);
      }
    );
  }

  call<T>(
    service: string,
    type: any = Object,
    method = 'get',
    params: any = {}
  ): Observable<Response> {
    const serviceParams = method === 'get' ? { params } : params;

    let request$: Observable<any>;

    switch (method) {
      case 'post':
        request$ = this.http.post(this.built(service), serviceParams);
        break;
      case 'put':
        request$ = this.http.put(this.built(service), serviceParams);
        break;
      case 'delete':
        request$ = this.http.delete(this.built(service), serviceParams);
        break;
      default:
        request$ = this.http.get(this.built(service), serviceParams);
        break;
    }

    return request$.pipe(map((data: any) => this.parse(data, type)));
  }

  open(service: string, finish: (res: boolean) => void, isFull = false) {
    let url = service;

    if (!isFull) {
      url = this.built(service);
    }

    const options = {
      observe: 'response' as 'body',
      responseType: 'blob' as 'json',
    };

    this.http.get<HttpResponse<any>>(url, options).subscribe({
      next: (response: any) => {
        if (response.body.type == 'application/json') {
          let reader = new FileReader();

          reader.addEventListener('loadend', (e: any) => {
            let json = JSON.parse(e.srcElement.result);
            alert(json.message.join('\n'));
          });

          reader.readAsText(response.body);

          return;
        }

        let hdrs = response.headers.get('Content-Disposition');
        let file = 'document';
        if (hdrs != null) {
          const split = hdrs.split('filename=');

          if (split.length > 0) {
            file = split[1].split('"').join('');
          }
        }

        const u = window.URL.createObjectURL(response.body);
        finish(true);
        window.open(u, '_blank');
      },
      error: (error: any) => {
        console.error(error);
        finish(false);
      },
    });
  }

  downloadSimple(service: string) {
    const options = {
      observe: 'response' as 'body',
      responseType: 'blob' as 'json',
    };

    this.http
      .get<HttpResponse<any>>(this.built(service), options)
      .subscribe((response: any) => {
        if (response.body.type == 'application/json') {
          let reader = new FileReader();

          reader.addEventListener('loadend', (e: any) => {
            let json = JSON.parse(e.srcElement.result);
            alert(json.message.join('\n'));
          });

          reader.readAsText(response.body);

          return;
        }

        let hdrs = response.headers.get('Content-Disposition');
        let file = 'document';
        if (hdrs != null) {
          const split = hdrs.split('filename=');
          if (split.length > 0) {
            file = split[1].split('"').join('');
          }
        }

        const u = window.URL.createObjectURL(response.body);
        const l = document.createElement('a');
        l.href = u;
        l.download = file;
        l.click();
      });
  }

  download(
    service: string,
    finish: (success: boolean) => void,
    isFull = false,
    name = 'document.pdf',
    type = 'pdf'
  ) {
    let url = service;

    if (!isFull) {
      url = this.built(service);
    }

    const options = {
      observe: 'response' as 'body',
      responseType: 'blob' as 'json',
    };

    this.http.get<HttpResponse<any>>(url, options).subscribe({
      next: (response: any) => {
        if (response.body.type == 'application/json') {
          let reader = new FileReader();

          reader.addEventListener('loadend', (e: any) => {
            let json = JSON.parse(e.srcElement.result);
            alert(json.message.join('\n'));
          });

          reader.readAsText(response.body);

          finish(false);

          return;
        }

        let hdrs = response.headers.get('Content-Disposition');
        let file = 'document';
        if (hdrs != null) {
          let split = hdrs.split('filename=');

          if (split.length > 0) {
            file = split[1].split('"').join('');
          }
        } else {
          file += '.' + type;
        }

        let u = window.URL.createObjectURL(response.body);
        let l = document.createElement('a');
        l.href = u;
        l.download = name;
        l.click();
        finish(true);
      },
      error: (error: any) => {
        console.error(error);

        let errorMsg = error.error.message
          ? error.error.message
          : error.message;
        alert(errorMsg);
        finish(false);
      },
    });
  }
}
